/**
 * @module Pages/AdminPages/AdminComponents/PartnerVsDirectRevenueChart
 * @description This module contains the PartnerVsDirectRevenueChart component used to display a pie chart comparing 
 * revenue from partners and direct individual revenue.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Chart from 'chart.js/auto';
import { Box, Card, Typography, useTheme } from '@mui/material';
/**
 * A chart component that displays the revenue comparison between partner revenue and direct individual revenue in a pie chart format.
 * 
 * The component fetches revenue data from the `/admin/partner-vs-direct-revenue` API endpoint, which provides data for
 * both partner and direct individual revenues. This data is then used to render a pie chart showing the relative contributions
 * of partner and direct individual revenue.
 * 
 * While the data is being fetched, a loading skeleton is shown. Once the data is fetched, the pie chart is displayed within
 * a `Card` component. The chart includes tooltips with the revenue value displayed in Indian Rupees (₹) and a legend at
 * the bottom to differentiate the two revenue types.
 *
 * @component
 * @returns {JSX.Element} A loading skeleton while data is being fetched, or a pie chart displaying the revenue comparison.
 */
const PartnerVsDirectRevenueChart = () => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const [revenueData, setRevenueData] = useState({});
  /**
   * Fetches revenue data for the partner vs direct revenue comparison.
   * 
   * This effect runs once when the component mounts. It makes a GET request to the `/admin/partner-vs-direct-revenue`
   * endpoint and updates the state with the retrieved revenue data (partner revenue and direct individual revenue).
   */
  useEffect(() => {
    const fetchRevenueData = async () => {
      try {
        const response = await axios.get('/admin/partner-vs-direct-revenue');
        const { partnerRevenue, directIndividualRevenue } = response.data;
        setRevenueData({ partnerRevenue, directIndividualRevenue });
      } catch (error) {
        console.error('Error fetching revenue data:', error);
      }
    };

    fetchRevenueData();
  }, []);
  /**
   * Creates and updates the pie chart when revenue data changes.
   * 
   * This effect runs whenever `revenueData` changes. It initializes the chart.js instance and renders a pie chart with
   * the provided revenue data. The chart is rendered inside a `canvas` element, and the chart is destroyed and reinitialized
   * if there is an existing instance when the component re-renders.
   */
  useEffect(() => {
    let myChart = null;

    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      // Destroy the existing Chart instance if it exists
      if (myChart) {
        myChart.destroy();
      }

      // Create a new Chart instance
      myChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: ['Partners Revenue', 'Direct Individual Revenue'],
          datasets: [
            {
              label: 'Partner vs Direct Revenue',
              data: [revenueData.partnerRevenue, revenueData.directIndividualRevenue],
              backgroundColor: ['#ba3f13', '#f15d27'],
              borderColor: '#ffffff',
              borderWidth: 1,
            },
          ],
        },
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label;
                  const value = context.formattedValue;
                  return `${label}: ₹${value}`;
                },
              },
            },
            legend: {
              display: true,
              position: 'bottom',
              align: 'end', // Aligns the legend to the right
            },
          },
        },
      });
    }

    // Cleanup function to destroy the Chart instance when the component unmounts
    return () => {
      if (myChart) {
        myChart.destroy();
      }
    };
  }, [revenueData]);

  return (
    <Card
      sx={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        width: '100%',
        backgroundColor: theme.palette.grey[200],
        borderRadius: '15px',
        boxShadow:'none'
      }}
    >
      <Typography variant="heading1">Partner Revenue V/S Direct Individual Revenue</Typography>
      <Box sx={{ height: '300px' }}>
        <canvas ref={chartRef} style={{ height: '300px', width: '100%' }} />
      </Box>
    </Card>
  );
};

export default PartnerVsDirectRevenueChart;
